import { useEffect, useMemo, useRef, useState } from 'react'
import mapboxgl, { type Map as MapboxMap, type Marker } from 'mapbox-gl'
import type { LineString } from 'geojson'
import type { GeometryResponse, Waypoint } from '@xingroute/schema'
import {
  GRADE_BUCKETS,
  GRADE_BUCKET_STOPS,
  smoothPathElevation,
  subTrackByDistance,
  toColouredSegments,
  trackBounds,
  type TrackPoint as LocalTrackPoint,
} from '../lib/gpx.js'
import { filterFiniteWaypoints, isFiniteLngLat, isFinitePoint } from '../lib/coords.js'
import { snapWithinThreshold } from '../lib/snapToTrack.js'
import { TRAIL_COLOR_PUBLISHED } from '../lib/trailColor.js'
import { useTheme } from '../theme/ThemeContext.js'
import { MapStyleSwitcher } from './MapStyleSwitcher.js'
import { BasemapToggle } from './BasemapToggle.js'
import {
  waypointGlyphKind,
  waypointGlyphSvgMarkup,
  type WaypointGlyphKind,
} from './ui/IconButton.js'
import {
  computeMergedRows,
  SYSTEM_KIND_TO_SYM,
  type EditableWaypoint,
} from '../lib/mergedWaypoints.js'
import { useElevationCursor } from '../hooks/useElevationCursor.js'
import { useBasemapProvider } from '../hooks/useBasemapProvider.js'
import { disableMapboxTelemetry } from '../lib/mapboxTelemetry.js'
import { buildTiandituStyle, type TiandituStyleKey } from './tianditu/tiandituStyle.js'
import { useLocale } from '../locale/LocaleContext.js'
import { api } from '../api/client.js'

// Mapbox public token comes from the Vite build env. The legacy
// VITE_MAPBOX_KEY name is preserved so the deploy workflow's
// existing secret wiring keeps working.
const MAPBOX_TOKEN = (import.meta.env.VITE_MAPBOX_KEY ?? '').trim()
if (MAPBOX_TOKEN) {
  mapboxgl.accessToken = MAPBOX_TOKEN
}

export type MapStyle = 'standard' | 'outdoor' | 'satellite'

interface StyleDef {
  label: string
  url: string
  /** Whether the style supports our custom contour overlay. We always layer
   *  it ourselves rather than relying on built-in contour layers. */
  contours: boolean
  /** Whether the style benefits from a 3D terrain DEM. All Standard family
   *  styles do; we still expose the flag to make the wiring explicit. */
  terrain: boolean
}

/** All three styles live in the Mapbox Standard family so theme switching
 *  stays a single setConfigProperty call. `satellite` uses a different
 *  style URL (Standard Satellite) because it ships satellite imagery;
 *  the other two share `mapbox://styles/mapbox/standard` and differ
 *  only via STANDARD_CONFIGS below. The legacy `minimal` style (monochrome
 *  Standard) was removed 2026-06-13 because it has no parity story under
 *  TianDiTu (天地图无 monochrome filter) — users now pick from 3 styles
 *  that map cleanly across both providers. */
const STYLES: Record<MapStyle, StyleDef> = {
  standard: {
    label: '标准',
    url: 'mapbox://styles/mapbox/standard',
    contours: false,
    terrain: true,
  },
  outdoor: {
    label: '户外',
    url: 'mapbox://styles/mapbox/standard',
    contours: true,
    terrain: true,
  },
  satellite: {
    label: '卫星',
    url: 'mapbox://styles/mapbox/standard-satellite',
    contours: false,
    terrain: true,
  },
}

/** Per-style Standard config properties applied via setConfigProperty.
 *  Standard Satellite quietly ignores `theme` and `show3dObjects` (per
 *  Mapbox docs); we still emit those values for unity and wrap every
 *  call in try/catch so the SDK can no-op safely. */
type StandardThemeName = 'default' | 'faded' | 'monochrome'
interface StandardConfig {
  theme: StandardThemeName
  showPlaceLabels: boolean
  showRoadLabels: boolean
  showPointOfInterestLabels: boolean
  showTransitLabels: boolean
  showLandmarkIcons: boolean
  show3dObjects: boolean
  showPedestrianRoads: boolean
}
const STANDARD_CONFIGS: Record<MapStyle, StandardConfig> = {
  standard: {
    theme: 'default',
    showPlaceLabels: true,
    showRoadLabels: true,
    showPointOfInterestLabels: true,
    showTransitLabels: true,
    showLandmarkIcons: true,
    show3dObjects: true,
    showPedestrianRoads: false,
  },
  outdoor: {
    theme: 'faded',
    showPlaceLabels: true,
    showRoadLabels: true,
    showPointOfInterestLabels: false,
    showTransitLabels: false,
    showLandmarkIcons: false,
    show3dObjects: false,
    showPedestrianRoads: true,
  },
  satellite: {
    // theme / show3dObjects ignored by Standard Satellite (Mapbox docs).
    // We pass them anyway and rely on the SDK to no-op; the try/catch in
    // applyBasemapConfig prevents any throw from breaking subsequent calls.
    theme: 'default',
    showPlaceLabels: true,
    showRoadLabels: true,
    showPointOfInterestLabels: false,
    showTransitLabels: false,
    showLandmarkIcons: false,
    show3dObjects: true,
    showPedestrianRoads: false,
  },
}

function applyBasemapConfig(map: MapboxMap, key: MapStyle, effective: 'light' | 'dark'): void {
  const cfg = STANDARD_CONFIGS[key]
  const settings: Array<[string, unknown]> = [
    ['lightPreset', effective === 'dark' ? 'night' : 'day'],
    ['theme', cfg.theme],
    ['showPlaceLabels', cfg.showPlaceLabels],
    ['showRoadLabels', cfg.showRoadLabels],
    ['showPointOfInterestLabels', cfg.showPointOfInterestLabels],
    ['showTransitLabels', cfg.showTransitLabels],
    ['showLandmarkIcons', cfg.showLandmarkIcons],
    ['show3dObjects', cfg.show3dObjects],
    ['showPedestrianRoads', cfg.showPedestrianRoads],
  ]
  for (const [prop, value] of settings) {
    try {
      map.setConfigProperty('basemap', prop, value)
    } catch {
      // Some properties (theme / show3dObjects) are no-ops on Standard
      // Satellite; an older SDK may throw rather than silently skip.
    }
  }
}

const TRACK_SOURCE = 'xr-track'
const TRACK_LAYER = 'xr-track-line'
const TRACK_CASING_LAYER = 'xr-track-casing'
// Per `wiki/entities/web-route-detail-page.md` §Elevation segment selection,
// brushing a sub-range on the elevation chart paints the same sub-polyline on
// the map with a brand-coloured stroke + white casing so the user can see at
// a glance where the selection sits along the route.
const TRACK_SELECTION_SOURCE = 'xr-track-selection'
const TRACK_SELECTION_CASING_LAYER = 'xr-track-selection-casing'
const TRACK_SELECTION_LAYER = 'xr-track-selection-line'
const TRACK_SELECTION_ENDS_SOURCE = 'xr-track-selection-ends'
const TRACK_SELECTION_ENDS_LAYER = 'xr-track-selection-ends-circle'

interface CursorIndex {
  points: LocalTrackPoint[]
  distancesKm: number[]
  totalKm: number
}

// Theme-aware overlay colors. Per ADR 06 the casing acts as a halo around the
// (always-coloured-by-grade) track line: on light basemaps a soft white halo
// gives the line air; on dark / satellite a dark ink halo punches it forward.
// Contour lines stay neutral so they don't fight the palette.
function casingColorFor(theme: 'light' | 'dark'): string {
  return theme === 'dark' ? 'rgba(20, 24, 18, 0.55)' : 'rgba(255, 255, 255, 0.70)'
}
function contourColorFor(theme: 'light' | 'dark'): string {
  return theme === 'dark' ? 'rgba(156, 163, 175, 0.40)' : 'rgba(75, 85, 99, 0.40)'
}

function toRad(d: number): number {
  return (d * Math.PI) / 180
}

function haversine(a: LocalTrackPoint, b: LocalTrackPoint): number {
  const dLat = toRad(b.lat - a.lat)
  const dLon = toRad(b.lon - a.lon)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

function buildCursorIndex(points: LocalTrackPoint[]): CursorIndex | null {
  if (points.length < 2) return null
  const distancesKm: number[] = [0]
  let totalM = 0
  for (let i = 1; i < points.length; i++) {
    totalM += haversine(points[i - 1]!, points[i]!)
    distancesKm.push(totalM / 1000)
  }
  return { points, distancesKm, totalKm: totalM / 1000 }
}

function pointAtKm(index: CursorIndex, km: number): [number, number] | null {
  if (!Number.isFinite(km) || km < 0 || km > index.totalKm) return null
  let lo = 0
  let hi = index.distancesKm.length - 1
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2)
    if (index.distancesKm[mid]! < km) lo = mid + 1
    else hi = mid
  }
  const rightIdx = Math.max(1, lo)
  const leftIdx = rightIdx - 1
  const left = index.points[leftIdx]!
  const right = index.points[rightIdx]!
  const leftKm = index.distancesKm[leftIdx]!
  const rightKm = index.distancesKm[rightIdx]!
  const t = rightKm > leftKm ? Math.min(1, Math.max(0, (km - leftKm) / (rightKm - leftKm))) : 0
  return [left.lon + (right.lon - left.lon) * t, left.lat + (right.lat - left.lat) * t]
}

function cursorKmFromFeature(
  event: mapboxgl.MapLayerMouseEvent,
  feature: mapboxgl.MapboxGeoJSONFeature,
): number | null {
  // Defensive: synthetic / programmatic events occasionally fire without a
  // `lngLat` payload (Mapbox internal animation taps, touch fallback, etc.).
  // Reading `.lng` on undefined throws the classic
  // "Cannot read properties of undefined (reading 'lng')" — which used to
  // bubble all the way to the route-detail ErrorBoundary. See
  // `wiki/pitfalls/routemap-undefined-waypoint-lnglat.md`.
  const evLngLat = event.lngLat
  if (!evLngLat || !isFiniteLngLat(evLngLat.lng, evLngLat.lat)) return null
  const props = feature.properties ?? {}
  const startKm = Number(props.startKm)
  const endKm = Number(props.endKm)
  if (!Number.isFinite(startKm) || !Number.isFinite(endKm)) return null
  const geometry = feature.geometry as LineString
  const coords = geometry.coordinates
  if (coords.length < 2) return (startKm + endKm) / 2
  // Walk every segment of the feature and find the closest projection to
  // the mouse point. Previous implementation collapsed the entire feature
  // to a single first-to-last chord, which introduced visible offset
  // between the live cursor marker and the rendered polyline on winding
  // segments. See wiki/pitfalls/routemap-cursor-track-offset.md.
  const point: [number, number] = [evLngLat.lng, evLngLat.lat]
  const segLengths: number[] = []
  let total = 0
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1] as [number, number]
    const b = coords[i] as [number, number]
    if (!a || !b || !isFiniteLngLat(a[0], a[1]) || !isFiniteLngLat(b[0], b[1])) {
      segLengths.push(0)
      continue
    }
    const meanLat = toRad((a[1] + b[1]) / 2)
    const dx = (b[0] - a[0]) * Math.cos(meanLat)
    const dy = b[1] - a[1]
    const len = Math.sqrt(dx * dx + dy * dy)
    segLengths.push(len)
    total += len
  }
  if (total <= 0) return (startKm + endKm) / 2
  let best = { segIdx: 0, t: 0, distSq: Infinity }
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1] as [number, number]
    const b = coords[i] as [number, number]
    if (!a || !b) continue
    const meanLat = toRad((a[1] + b[1]) / 2)
    const ax = a[0] * Math.cos(meanLat)
    const ay = a[1]
    const bx = b[0] * Math.cos(meanLat)
    const by = b[1]
    const px = point[0] * Math.cos(meanLat)
    const py = point[1]
    const dx = bx - ax
    const dy = by - ay
    const len2 = dx * dx + dy * dy
    let t = 0
    if (len2 > 0) {
      t = Math.min(1, Math.max(0, ((px - ax) * dx + (py - ay) * dy) / len2))
    }
    const projX = ax + dx * t
    const projY = ay + dy * t
    const ddx = projX - px
    const ddy = projY - py
    const distSq = ddx * ddx + ddy * ddy
    if (distSq < best.distSq) {
      best = { segIdx: i - 1, t, distSq }
    }
  }
  // Accumulate length along the polyline up to the best projection.
  let accum = 0
  for (let i = 0; i < best.segIdx; i++) accum += segLengths[i] ?? 0
  accum += (segLengths[best.segIdx] ?? 0) * best.t
  const ratio = accum / total
  return startKm + (endKm - startKm) * ratio
}

function makeCursorMarkerElement(): HTMLElement {
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  el.style.width = '18px'
  el.style.height = '18px'
  el.style.borderRadius = '9999px'
  el.style.background = 'rgba(230, 103, 37, 0.24)'
  el.style.border = '2px solid #ffffff'
  el.style.boxShadow = '0 0 0 2px rgba(26, 90, 68, 0.9), 0 6px 16px rgba(0,0,0,0.28)'
  el.style.pointerEvents = 'none'
  return el
}

// Read a CSS custom property off :root with a fallback. Used for waypoint
// marker colors so they follow the active theme (Mapbox's built-in Marker
// only accepts a literal color string, so we resolve the token at render
// time and rebuild markers on theme switches).
function readCssVar(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

// Build the DOM element for a waypoint marker. Mapbox markers are
// imperative (outside React), so we hand-render the same glyph the
// read-view list shows via `waypointGlyphSvgMarkup`, wrapped in a small
// circular badge for legibility against any basemap. The glyph is
// monochrome — shape (flag / triangle / footprint / photo) carries the
// type, not color — so the map matches the side list exactly.
//
// The badge lives in a child <span data-xr-badge> so the focus highlight
// can scale it without fighting Mapbox's positioning transform on the
// outer element.
export function makeWaypointMarkerElement(kind: WaypointGlyphKind, size: number = 24): HTMLElement {
  const el = document.createElement('div')
  const brandGreen = readCssVar('--xr-brand-primary', '#1a5a44')
  const h = Math.round(size * (32 / 24))
  const innerR = Math.round(size * (5 / 24))
  const cy = Math.round(size * (11 / 24))
  const setSvg = (svg: string) => {
    const badge = document.createElement('span')
    badge.dataset.xrBadge = 'true'
    badge.style.display = 'block'
    badge.style.width = `${size}px`
    badge.style.height = `${h}px`
    badge.style.lineHeight = '0'
    badge.style.transition =
      'transform 150ms ease-out, opacity 150ms ease-out, box-shadow 150ms ease-out'
    badge.style.transformOrigin = '50% 100%'
    badge.innerHTML = svg
    el.replaceChildren(badge)
  }
  // For larger markers (28+), embed the kind glyph inside the pin
  if (size >= 28 && kind !== 'generic' && kind !== 'photo') {
    const glyphSize = Math.round(size * 0.45)
    const glyphY = cy - glyphSize / 2
    const glyphX = size / 2 - glyphSize / 2
    setSvg(`<svg width="${size}" height="${h}" viewBox="0 0 ${size} ${h}" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M${size / 2} 0C${size * 0.225} 0 0 ${size * 0.225} 0 ${size / 2}c0 ${size * 0.375} ${size / 2} ${h - size / 2} ${size / 2} ${h - size / 2}s${size / 2}-${h - size} ${size / 2}-${h - size / 2}C${size} ${size * 0.225} ${size * 0.775} 0 ${size / 2} 0z" fill="${brandGreen}"/>
      <circle cx="${size / 2}" cy="${cy}" r="${innerR}" fill="white"/>
      <g transform="translate(${glyphX},${glyphY})" stroke="${brandGreen}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <svg viewBox="0 0 24 24" width="${glyphSize}" height="${glyphSize}">${waypointGlyphSvgMarkup(kind, glyphSize, brandGreen).replace(/<svg[^>]*>|<\/svg>/g, '')}</svg>
      </g>
    </svg>`)
  } else {
    setSvg(`<svg width="${size}" height="${h}" viewBox="0 0 24 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="${brandGreen}"/>
      <circle cx="12" cy="11" r="5" fill="white"/>
    </svg>`)
  }
  el.dataset.xrAnchor = 'bottom-tip'
  el.style.display = 'block'
  el.style.width = `${size}px`
  el.style.height = `${h}px`
  el.style.lineHeight = '0'
  el.style.transition = 'transform 150ms ease-out'
  el.style.cursor = 'pointer'
  return el
}
const DEM_SOURCE = 'xr-dem'
const CONTOUR_SOURCE = 'xr-contours'
const CONTOUR_LAYER = 'xr-contour-lines'

export interface RouteMapProps {
  geometry: GeometryResponse | null
  replayPoint?: { lon: number; lat: number } | null
  /** RouteEditor can force waypoint editing while persisting through
   *  `onWaypointsStateChange` + the editor's normal save flow. */
  editing?: boolean
  onWaypointsStateChange?: (state: { editing: boolean; waypoints: Waypoint[] }) => void
  /** External request to fly the map to a specific waypoint and open its
   *  popup. The nonce lets the parent re-fire the same id (e.g. two list
   *  clicks on the same waypoint). */
  focusWaypoint?: { id: string; nonce: number } | null
  /** Show waypoint markers on the map. Defaults to false; editing mode
   *  always forces them visible regardless of this flag. */
  showWaypointsOnMap?: boolean
  /** Multi-select pinned waypoints. When this prop is provided (even as []),
   *  RouteMap switches to "pin interaction mode":
   *    - showWaypointsOnMap=true → render ALL waypoints (pinned ones use the
   *      pin colour, others use default).
   *    - showWaypointsOnMap=false → render only the pinned set.
   *    - focus DOES NOT pan or fly the map.
   *  Editor callers (RouteEditor) omit this prop entirely to keep classic
   *  behaviour: any focus shows all markers, focus flyTo. */
  pinnedWaypoints?: string[]
  /** The waypoint row that is active in the side list; only this marker is draggable in edit mode. */
  activeWaypointId?: string | null
  onWaypointClick?: (id: string) => void
  /** Waypoint ids that carry at least one attachment. Generic markers in
   *  this set render the photo glyph instead of the footprint, matching
   *  the read-view list. */
  attachmentWaypointIds?: ReadonlySet<string>
  cursorKm?: number
  /** External keyboard shortcut request to switch basemap preset. */
  styleRequest?: { style: MapStyle; nonce: number } | null
  onStyleChange?: (style: MapStyle) => void
  /** Editor "click-to-add-waypoint" one-shot mode. While true the cursor
   *  becomes a crosshair; the next click is reported to the parent. Off-track
   *  waypoints are allowed; clicks within 5 m of the route are snapped to it. */
  addingWaypoint?: boolean
  /** Fired when the user clicks the map while {@link addingWaypoint} is on. */
  onWaypointAddRequest?: (result: { ok: true; lat: number; lon: number; ele?: number }) => void
}

export function RouteMap({
  geometry,
  replayPoint,
  editing = false,
  onWaypointsStateChange,
  focusWaypoint,
  showWaypointsOnMap = false,
  pinnedWaypoints,
  activeWaypointId,
  onWaypointClick,
  attachmentWaypointIds,
  cursorKm: cursorKmProp,
  styleRequest,
  onStyleChange,
  addingWaypoint = false,
  onWaypointAddRequest,
}: RouteMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<MapboxMap | null>(null)
  const replayMarkerRef = useRef<Marker | null>(null)
  const markersRef = useRef<Map<string, Marker>>(new globalThis.Map())
  const waypointDragHandlersRef = useRef<Map<string, () => void>>(new globalThis.Map())
  const draftWaypointsRef = useRef<Waypoint[]>([])
  const draftGeometryRef = useRef<GeometryResponse | null>(null)
  const waypointEditContextRef = useRef<{
    geometry: GeometryResponse | null
    editing: boolean
    onWaypointsStateChange?: (state: { editing: boolean; waypoints: Waypoint[] }) => void
  }>({ geometry, editing, onWaypointsStateChange })
  const cursorMarkerRef = useRef<Marker | null>(null)
  // Track which geometry instance we've already auto-fitted to, so subsequent
  // style swaps don't reset the user's pan / zoom every time.
  const fittedGeometryRef = useRef<GeometryResponse | null>(null)
  const { effective } = useTheme()
  const effectiveRef = useRef(effective)
  effectiveRef.current = effective
  const [style, setStyle] = useState<MapStyle>('standard')
  const [draftWaypoints, setDraftWaypoints] = useState<Waypoint[]>([])
  const [error, setError] = useState<string | null>(null)
  const { cursorKm, selectedRangeKm, setCursorKm } = useElevationCursor()
  const { provider } = useBasemapProvider()
  const { locale } = useLocale()

  // styleSource is either a Mapbox style URL (provider=mapbox) or a
  // raster style spec (provider=tianditu). mapbox-gl 3.x's `setStyle`
  // accepts both. The TianDiTu path emits raster sources per
  // `buildTiandituStyle`; see wiki/decisions/05-basemaps.md §Web
  // BasemapProvider for the 3-style mapping. Custom track / contour /
  // route overlays are re-added in `paintLayers` after `style.load`
  // fires (Standard config calls like `setConfigProperty` are skipped
  // under tianditu — they only exist on Mapbox Standard styles).
  const styleSource = useMemo<string | mapboxgl.StyleSpecification>(() => {
    if (provider === 'tianditu') {
      const spec = buildTiandituStyle(style as TiandituStyleKey)
      // Token missing → fall back to mapbox style URL so the map still
      // renders. The BasemapProvider context already prevents callers
      // from picking tianditu when token absent, but defend here too.
      if (!spec) return STYLES[style].url
      return spec as unknown as mapboxgl.StyleSpecification
    }
    return STYLES[style].url
  }, [provider, style])

  // Defensive filter: a single waypoint with undefined / NaN coords would
  // crash the whole map through `Marker.addTo` → `_update` →
  // `new LngLat(undefined, undefined)`. See `wiki/pitfalls/
  // routemap-undefined-waypoint-lnglat.md`.
  const rawLiveWaypoints =
    editing && draftGeometryRef.current === geometry ? draftWaypoints : (geometry?.points ?? [])
  const markerWaypoints = useMemo(() => {
    if (!geometry) return rawLiveWaypoints
    return computeMergedRows(rawLiveWaypoints as EditableWaypoint[], geometry.line).map((row) => {
      if (row.kind === 'user') return row.waypoint
      return {
        id: row.rowKey,
        lat: row.system.lat,
        lon: row.system.lon,
        ...(row.system.ele !== undefined ? { ele: row.system.ele } : {}),
        sym: SYSTEM_KIND_TO_SYM[row.system.kind],
        auto: true,
      } satisfies Waypoint
    })
  }, [editing, geometry, rawLiveWaypoints])
  const liveWaypoints = useMemo(
    () =>
      filterFiniteWaypoints(markerWaypoints, (wp) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn('[RouteMap] dropping waypoint with non-finite coords', {
            id: (wp as { id?: unknown }).id,
            lon: wp.lon,
            lat: wp.lat,
          })
        }
      }),
    [markerWaypoints],
  )
  const effectiveCursorKm = cursorKmProp ?? cursorKm
  const cursorIndex = useMemo(() => {
    if (!geometry) return null
    return buildCursorIndex(geometry.line.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele })))
  }, [geometry])

  useEffect(() => {
    if (styleRequest) setStyle(styleRequest.style)
  }, [styleRequest?.style, styleRequest?.nonce])

  useEffect(() => {
    onStyleChange?.(style)
  }, [onStyleChange, style])

  useEffect(() => {
    waypointEditContextRef.current = { geometry, editing, onWaypointsStateChange }
  }, [geometry, editing, onWaypointsStateChange])

  useEffect(() => {
    draftWaypointsRef.current = draftWaypoints
  }, [draftWaypoints])

  // Reset drafts when the underlying geometry shifts. The in-map edit toggle
  // is gone; callers that force `editing` own the save / discard affordance.
  useEffect(() => {
    const next = geometry?.points ?? []
    draftGeometryRef.current = geometry
    draftWaypointsRef.current = next
    setDraftWaypoints(next)
  }, [geometry])

  // Initialise / restyle map. Style switches preserve the camera (Mapbox's
  // setStyle keeps center/zoom/pitch/bearing); we explicitly do NOT trigger
  // fitBounds again — that only runs the first time geometry arrives.
  useEffect(() => {
    if (!containerRef.current) return
    if (!MAPBOX_TOKEN) {
      setError('Mapbox public token missing at build time (VITE_MAPBOX_KEY)')
      return
    }
    const onStyleReady = () => {
      // Apply Standard config (theme, lightPreset, label/feature toggles)
      // immediately so the new style URL doesn't briefly render in its
      // defaults before the per-style config effect catches up.
      // `setConfigProperty` is Mapbox Standard-only; under tianditu the
      // raster style has no Standard config, so we skip the call.
      try {
        if (mapRef.current && provider === 'mapbox') {
          applyBasemapConfig(mapRef.current, style, effective)
        }
        paintLayers()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] style.load handler failed', err)
      }
    }
    if (!mapRef.current) {
      mapRef.current = new mapboxgl.Map({
        container: containerRef.current,
        style: styleSource,
        center: [120, 30],
        zoom: 2,
        bearing: 0,
        attributionControl: false,
      })
      disableMapboxTelemetry(mapRef.current)
      // Mapbox ToS requires © Mapbox · OpenStreetMap attribution to be
      // visible. The global SiteFooter no longer carries the credit, so add
      // a compact AttributionControl on the map itself (small info button
      // bottom-right that expands to the full text). Added BEFORE
      // NavigationControl so — because Mapbox prepends into bottom-* — it
      // ends up at the very bottom of the bottom-right column.
      mapRef.current.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right')
      // NavigationControl sits at the bottom of the right column. The grade
      // gradient legend used to live above it as a second IControl, but is
      // now an absolute overlay along the bottom edge of the map (see
      // `xr-route-map-grade-legend` block in the JSX) so it can span 61.8%
      // of the map width without distorting the right-side control stack.
      mapRef.current.addControl(
        new mapboxgl.NavigationControl({ showCompass: true, visualizePitch: true }),
        'bottom-right',
      )
      // ScaleControl shares the bottom-left container with the Mapbox
      // logo + attribution; the scoped CSS in styles.css flips that
      // container to flex-row-reverse so logo lands on the left and
      // scale on the right, with `align-items: center` keeping their
      // visual centres on the same horizontal baseline.
      mapRef.current.addControl(
        new mapboxgl.ScaleControl({ maxWidth: 120, unit: 'metric' }),
        'bottom-left',
      )
      mapRef.current.on('style.load', onStyleReady)
    } else {
      mapRef.current.setStyle(styleSource)
      mapRef.current.once('style.load', onStyleReady)
    }
    return undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleSource])

  // Apply Standard basemap config (theme / lightPreset / label visibility /
  // showPedestrianRoads / show3dObjects) whenever the chosen style key OR
  // the effective theme changes. This handles BOTH the cross-Standard-key
  // switch (minimal ↔ standard ↔ outdoor share the same style URL, so the
  // style-URL effect above does NOT fire) AND theme toggles. We also
  // re-paint our own casing + contour layers in case the theme flipped.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (provider !== 'mapbox') return
    const apply = () => {
      try {
        applyBasemapConfig(map, style, effective)
        if (map.getLayer(TRACK_CASING_LAYER)) {
          map.setPaintProperty(TRACK_CASING_LAYER, 'line-color', casingColorFor(effective))
        }
        if (map.getLayer(CONTOUR_LAYER)) {
          map.setPaintProperty(CONTOUR_LAYER, 'line-color', contourColorFor(effective))
        }
        // paintLayers handles add/remove of the contour layer when the style
        // key change toggles `contours`. Safe to re-run (idempotent).
        paintLayers()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] basemap config apply failed', err)
      }
    }
    if (map.isStyleLoaded()) apply()
    else map.once('style.load', apply)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style, effective, provider])

  // (style + tiles fully loaded) when the map isn't yet ready. Mapbox raster
  // tiles stay behind the service-worker offline cache; style JSON itself
  // stays on the StaleWhileRevalidate runtime cache.
  useEffect(() => {
    if (!geometry || !mapRef.current) return
    const map = mapRef.current
    const run = () => {
      try {
        paintLayers()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] paintLayers (geometry change) failed', err)
      }
    }
    if (map.isStyleLoaded()) run()
    else map.once('idle', run)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometry, styleSource])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!replayPoint) {
      replayMarkerRef.current?.remove()
      replayMarkerRef.current = null
      return
    }
    // Defensive: replayPoint comes from the parent (e.g. /tracks/:id
    // playback). Skip the marker entirely if its coords are non-finite —
    // otherwise `Marker.addTo` would synchronously throw from inside the
    // Mapbox SDK and unmount the whole map subtree.
    if (!isFinitePoint(replayPoint)) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] dropping replayPoint with non-finite coords', replayPoint)
      }
      replayMarkerRef.current?.remove()
      replayMarkerRef.current = null
      return
    }
    // CRITICAL: call setLngLat BEFORE addTo. See cursor marker comment above
    // and wiki/pitfalls/routemap-marker-addto-before-setlnglat.md — addTo
    // synchronously runs Marker._update which reads `this._lngLat.lng`, so
    // a marker that hasn't been positioned yet throws on addTo.
    if (!replayMarkerRef.current) {
      try {
        const el = document.createElement('div')
        el.className =
          'flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-[var(--xr-accent-fill)] text-sm shadow-[var(--xr-shadow-md)]'
        el.textContent = '🚶'
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
        marker.setLngLat([replayPoint.lon, replayPoint.lat])
        marker.addTo(map)
        replayMarkerRef.current = marker
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] replay marker init failed', { replayPoint, err })
        return
      }
    } else {
      try {
        replayMarkerRef.current.setLngLat([replayPoint.lon, replayPoint.lat])
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] replay marker setLngLat failed', { replayPoint, err })
        replayMarkerRef.current.remove()
        replayMarkerRef.current = null
      }
    }
  }, [replayPoint, geometry])

  // Render waypoint markers.
  //
  // Two interaction modes:
  // 1) Classic (RouteEditor): `pinnedWaypoints` prop omitted. Any hover/focus
  //    makes ALL markers visible — preserves the long-standing editor UX.
  // 2) Pin (RouteDetailPage): `pinnedWaypoints` prop provided. Marker
  //    visibility = (showWaypointsOnMap ? all : pinned ∪ {hovered}).
  //    Colour tier: pin (brand) > hover (accent) > default (brand muted).
  //
  // Edit mode forces all markers visible & draggable regardless of mode.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const existing = markersRef.current
    const next = new globalThis.Map<string, Marker>()
    const pinMode = pinnedWaypoints !== undefined
    const pinnedSet = new globalThis.Set<string>(pinnedWaypoints ?? [])

    let visibleIds: globalThis.Set<string> | null // null = "all"
    if (editing) {
      // Editor: honour the show-all toggle, but always keep the focused
      // waypoint visible so a selected row's pin is reachable for drag.
      if (showWaypointsOnMap) {
        visibleIds = null
      } else {
        const set = new globalThis.Set<string>()
        if (focusWaypoint) set.add(focusWaypoint.id)
        visibleIds = set
      }
    } else if (pinMode) {
      if (showWaypointsOnMap) {
        visibleIds = null
      } else {
        visibleIds = new globalThis.Set<string>(pinnedSet)
      }
    } else {
      // Classic: focus reveals all
      const reveal = showWaypointsOnMap || focusWaypoint != null
      visibleIds = reveal ? null : new globalThis.Set<string>()
    }

    const list = liveWaypoints.filter((wp) => visibleIds === null || visibleIds.has(wp.id))
    for (const wp of list) {
      // `liveWaypoints` is already filtered by `filterFiniteWaypoints`, but
      // we re-check here as defense in depth — a regression upstream that
      // sneaks a non-finite coord through would otherwise crash the whole
      // map subtree via Mapbox's late `.lng` validation.
      if (!isFinitePoint(wp)) continue
      const kind = waypointGlyphKind(wp.sym, attachmentWaypointIds?.has(wp.id) ?? false, wp.kind)
      // Pin-mode non-pinned waypoints fade so the pinned subset dominates.
      // Show-all is an intentional "all selected/displayed" state, so keep every marker full-strength.
      const dimmed = pinMode && !editing && !showWaypointsOnMap && !pinnedSet.has(wp.id)
      const prev = existing.get(wp.id)
      // The marker element is built once per glyph kind; the edit↔view flip
      // is tracked too so the read-mode popup is naturally dropped on the
      // way into edit mode (Mapbox keeps a bound popup otherwise). Recreate
      // only when the glyph kind or edit state changes.
      const prevSig = prev ? (prev as unknown as { _xrSig?: string })._xrSig : undefined
      const sig = `${kind}|${editing ? 'e' : 'v'}|${effective}`
      let marker = prev
      if (!marker || prevSig !== sig) {
        if (prev) prev.remove()
        marker = new mapboxgl.Marker({
          element: makeWaypointMarkerElement(kind, 24),
          draggable: editing,
          anchor: 'bottom',
        })
        ;(marker as unknown as { _xrSig?: string })._xrSig = sig
      }
      try {
        marker.setLngLat([wp.lon, wp.lat])
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] waypoint marker setLngLat failed', { wp, err })
        if (!prev) continue
        next.set(wp.id, marker)
        continue
      }
      const canDrag = editing && activeWaypointId === wp.id && !wp.auto
      marker.setDraggable(canDrag)
      const markerElement = marker.getElement()
      markerElement.style.cursor = canDrag ? 'grab' : 'pointer'
      markerElement.onclick = (event) => {
        event.stopPropagation()
        onWaypointClick?.(wp.id)
      }
      const badgeEl = markerElement.querySelector('[data-xr-badge]') as HTMLElement | null
      if (badgeEl) badgeEl.style.opacity = dimmed ? '0.45' : '1'
      // Popup behavior:
      //   - read mode: rebuild popup each render so name/desc edits show
      //     on the next open.
      //   - edit mode: NO popup attached. The user is already editing in
      //     the right-rail list; opening a redundant popup just blocks
      //     the map and competes with the row editor. Markers track the
      //     edit state in `_xrSig` and are recreated on the edit↔view flip
      //     so any popup the previous mode attached is naturally dropped.
      if (!editing) {
        marker.setPopup(makeWaypointPopup(wp))
      }
      if (!prev || prevSig !== sig) {
        marker.addTo(map)
      }
      const dragHandler = dragHandlerFor(wp.id)
      marker.off('dragend', dragHandler)
      if (canDrag) marker.on('dragend', dragHandler)
      // Focus highlight: when the user selects a row in the side list (or
      // clicks a marker), scale + drop-shadow the chosen badge so it pops
      // on the map. Apply on the inner badge wrapper so we don't fight
      // Mapbox's positioning transform on the outer container element.
      const isFocused = focusWaypoint?.id === wp.id || showWaypointsOnMap
      if (badgeEl) {
        if (isFocused) {
          badgeEl.style.transform = 'scale(1.35)'
          badgeEl.style.boxShadow = ''
        } else {
          badgeEl.style.transform = ''
          badgeEl.style.boxShadow = ''
        }
      }
      markerElement.style.zIndex = isFocused ? '10' : ''
      next.set(wp.id, marker)
    }
    for (const [id, m] of existing) {
      if (!next.has(id)) {
        const dragHandler = waypointDragHandlersRef.current.get(id)
        if (dragHandler) m.off('dragend', dragHandler)
        m.remove()
        waypointDragHandlersRef.current.delete(id)
      }
    }
    markersRef.current = next

    function dragHandlerFor(id: string) {
      let handler = waypointDragHandlersRef.current.get(id)
      if (handler) return handler
      handler = () => {
        const m = markersRef.current.get(id)
        if (!m) return
        const ll = m.getLngLat()
        if (!ll || !isFiniteLngLat(ll.lng, ll.lat)) return
        const ctx = waypointEditContextRef.current
        const trackPoints = ctx.geometry?.line ?? []
        const snapped =
          trackPoints.length > 0 ? snapWithinThreshold(ll.lat, ll.lng, trackPoints, 5) : null
        let patch: Partial<Waypoint>
        if (snapped) {
          // Move the on-screen marker back to the snapped point so the
          // user sees the rail bind happen immediately.
          m.setLngLat({ lng: snapped.lon, lat: snapped.lat })
          patch = { lat: snapped.lat, lon: snapped.lon, ele: snapped.ele }
        } else {
          patch = { lat: ll.lat, lon: ll.lng }
        }
        const nextWaypoints = draftWaypointsRef.current.map((w) =>
          w.id === id ? ({ ...w, ...patch } as Waypoint) : w,
        )
        draftWaypointsRef.current = nextWaypoints
        setDraftWaypoints(nextWaypoints)
        ctx.onWaypointsStateChange?.({
          editing: ctx.editing,
          waypoints: filterFiniteWaypoints(nextWaypoints),
        })
      }
      waypointDragHandlersRef.current.set(id, handler)
      return handler
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    liveWaypoints,
    geometry?.geometry_type,
    editing,
    showWaypointsOnMap,
    focusWaypoint,
    activeWaypointId,
    pinnedWaypoints,
    attachmentWaypointIds,
    effective,
    onWaypointClick,
  ])

  // Bidirectional elevation cursor: map line hover writes cursorKm back
  // to the shared context; profile hover reads the same signal and this effect
  // renders a non-interactive ghost marker at the interpolated route point.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !cursorIndex || effectiveCursorKm === null) {
      cursorMarkerRef.current?.remove()
      cursorMarkerRef.current = null
      return
    }
    const lngLat = pointAtKm(cursorIndex, effectiveCursorKm)
    // pointAtKm can interpolate to NaN if the underlying track contains
    // non-finite coords. Mapbox's `Marker.setLngLat` happily stores NaN, then
    // throws `Cannot read properties of undefined (reading 'lng')` on the
    // next `_update` — which lands in the page ErrorBoundary.
    if (!lngLat || !isFiniteLngLat(lngLat[0], lngLat[1])) {
      cursorMarkerRef.current?.remove()
      cursorMarkerRef.current = null
      return
    }
    // CRITICAL: call setLngLat BEFORE addTo. Marker.addTo synchronously runs
    // Marker._update which reads `this._lngLat.lng` — if _lngLat is undefined
    // (the marker was constructed without an initial coord), it throws
    // `Cannot read properties of undefined (reading 'lng')` and the throw
    // escapes any later try/catch around setLngLat. See
    // wiki/pitfalls/routemap-marker-addto-before-setlnglat.md.
    if (!cursorMarkerRef.current) {
      try {
        const marker = new mapboxgl.Marker({ element: makeCursorMarkerElement() })
        marker.setLngLat(lngLat)
        marker.addTo(map)
        cursorMarkerRef.current = marker
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] cursor marker init failed', { lngLat, err })
        return
      }
    } else {
      try {
        cursorMarkerRef.current.setLngLat(lngLat)
      } catch (err) {
        // Belt-and-suspenders: if Mapbox still rejects the value for any
        // reason (e.g. SDK upgrade tightens validation), drop the marker
        // rather than letting the throw take down the whole RouteMap subtree.
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] cursor marker setLngLat failed', { lngLat, err })
        cursorMarkerRef.current.remove()
        cursorMarkerRef.current = null
      }
    }
  }, [cursorIndex, effectiveCursorKm])

  // Selection highlight: paint the brushed sub-range from the elevation chart
  // as a brand-coloured polyline + endpoint dots on top of the grade-coloured
  // base track. We re-slice the geometry by cumulative distance via
  // `subTrackByDistance` (same fractional-segment math the chart uses for
  // segment stats), so the visual extent of the highlight matches the
  // numbers reported in the elevation caption. When the selection clears,
  // we replace both sources with empty FeatureCollections rather than
  // removing the layers — the layers stay in place so they survive style
  // swaps.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const trackSource = map.getSource(TRACK_SELECTION_SOURCE) as mapboxgl.GeoJSONSource | undefined
    const endsSource = map.getSource(TRACK_SELECTION_ENDS_SOURCE) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (!trackSource || !endsSource) return
    const emptyFC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }
    if (!selectedRangeKm || !geometry || geometry.line.length < 2) {
      trackSource.setData(emptyFC)
      endsSource.setData(emptyFC)
      return
    }
    const [fromKm, toKm] = selectedRangeKm
    const slice = subTrackByDistance(
      geometry.line.map((p) => ({ lat: p.lat, lon: p.lon, ele: p.ele })),
      fromKm * 1000,
      toKm * 1000,
    )
    if (!slice || slice.length < 2) {
      trackSource.setData(emptyFC)
      endsSource.setData(emptyFC)
      return
    }
    const coords: [number, number][] = slice
      .filter((p) => isFiniteLngLat(p.lon, p.lat))
      .map((p) => [p.lon, p.lat])
    if (coords.length < 2) {
      trackSource.setData(emptyFC)
      endsSource.setData(emptyFC)
      return
    }
    trackSource.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: coords },
        },
      ],
    })
    const [startCoord] = coords
    const endCoord = coords[coords.length - 1]
    endsSource.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: { kind: 'start' },
          geometry: { type: 'Point', coordinates: startCoord! },
        },
        {
          type: 'Feature',
          properties: { kind: 'end' },
          geometry: { type: 'Point', coordinates: endCoord! },
        },
      ],
    })
  }, [geometry, selectedRangeKm])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    let bound = false
    let frame: number | null = null
    let pendingKm: number | null = null

    const flush = () => {
      frame = null
      setCursorKm(pendingKm)
    }
    const onMove = (event: mapboxgl.MapLayerMouseEvent) => {
      if (addingWaypoint) return // Don't interfere with add-waypoint cursor
      try {
        const feature = event.features?.[0]
        if (!feature) return
        const nextKm = cursorKmFromFeature(event, feature)
        if (nextKm === null) return
        map.getCanvas().style.cursor = 'crosshair'
        pendingKm = nextKm
        if (frame === null) frame = requestAnimationFrame(flush)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] mousemove handler failed', err)
      }
    }
    const onLeave = () => {
      if (addingWaypoint) return // Don't reset cursor during add mode
      map.getCanvas().style.cursor = ''
      pendingKm = null
      if (frame !== null) cancelAnimationFrame(frame)
      frame = null
      setCursorKm(null)
    }
    const bind = () => {
      if (bound || !map.getLayer(TRACK_LAYER)) return
      map.on('mousemove', TRACK_LAYER, onMove)
      map.on('mouseleave', TRACK_LAYER, onLeave)
      bound = true
    }

    bind()
    if (!bound) map.once('idle', bind)
    return () => {
      if (frame !== null) cancelAnimationFrame(frame)
      map.off('idle', bind)
      if (bound) {
        try {
          map.off('mousemove', TRACK_LAYER, onMove)
          map.off('mouseleave', TRACK_LAYER, onLeave)
        } catch {
          // Style swaps can remove the layer before React cleans up.
        }
      }
    }
  }, [geometry, setCursorKm, styleSource, addingWaypoint])

  // Click-to-add (editor "addingWaypoint" one-shot mode). The detail page
  // omits both `addingWaypoint` and `onWaypointAddRequest` so this effect
  // becomes a no-op for read-only callers. Per
  // `wiki/decisions/10-route-spatial-and-governance.md` §I, the click is
  // Click-to-add waypoint: no range limit (water routes need arbitrary
  // placement). The click position is used directly; if a track exists
  // we find the nearest point for sorting but never reject.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (!addingWaypoint || !onWaypointAddRequest) return
    const canvas = map.getCanvas()
    const previousCursor = canvas.style.cursor
    canvas.style.cursor = 'crosshair'
    const handler = (e: mapboxgl.MapMouseEvent) => {
      const target = e.originalEvent.target as HTMLElement | null
      if (target?.closest('.mapboxgl-marker')) return
      if (!e.lngLat || !isFiniteLngLat(e.lngLat.lng, e.lngLat.lat)) return
      const snapped =
        geometry?.line && geometry.line.length > 0
          ? snapWithinThreshold(e.lngLat.lat, e.lngLat.lng, geometry.line, 5)
          : null
      if (snapped) {
        onWaypointAddRequest({ ok: true, lat: snapped.lat, lon: snapped.lon, ele: snapped.ele })
        return
      }
      onWaypointAddRequest({ ok: true, lat: e.lngLat.lat, lon: e.lngLat.lng })
    }
    map.on('click', handler)
    return () => {
      map.off('click', handler)
      canvas.style.cursor = previousCursor
    }
  }, [addingWaypoint, onWaypointAddRequest, geometry])

  function paintLayers() {
    const map = mapRef.current
    if (!map) return
    const opts = STYLES[style]

    if (opts.terrain) {
      if (!map.getSource(DEM_SOURCE)) {
        map.addSource(DEM_SOURCE, {
          type: 'raster-dem',
          url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
          tileSize: 512,
          maxzoom: 14,
        })
      }
      map.setTerrain({ source: DEM_SOURCE, exaggeration: 1.4 })
    } else {
      map.setTerrain(null)
    }

    if (opts.contours) {
      if (!map.getSource(CONTOUR_SOURCE)) {
        map.addSource(CONTOUR_SOURCE, {
          type: 'vector',
          url: 'mapbox://mapbox.mapbox-terrain-v2',
        })
      }
      if (!map.getLayer(CONTOUR_LAYER)) {
        // Index-aware width: contours whose elevation is a multiple of 500m
        // render as bold "indexed" lines; intermediate 100m lines stay thin.
        // This is the standard topographic-map convention. The slot:'bottom'
        // assignment ensures contours sit below all Standard labels.
        map.addLayer({
          id: CONTOUR_LAYER,
          type: 'line',
          source: CONTOUR_SOURCE,
          'source-layer': 'contour',
          slot: 'bottom',
          filter: ['>', ['get', 'ele'], 0],
          paint: {
            'line-color': contourColorFor(effective),
            'line-width': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              ['case', ['==', ['%', ['to-number', ['get', 'ele']], 500], 0], 0.8, 0.3],
              14,
              ['case', ['==', ['%', ['to-number', ['get', 'ele']], 500], 0], 1.5, 0.7],
            ],
            'line-emissive-strength': 0.6,
          },
          layout: { 'line-cap': 'round', 'line-join': 'round' },
        })
      }
    } else if (map.getLayer(CONTOUR_LAYER)) {
      // Style switched away from a contour-bearing key — remove the layer
      // so the next key change starts from a clean slate.
      map.removeLayer(CONTOUR_LAYER)
    }

    if (!geometry || geometry.line.length < 2) {
      if (map.getLayer(TRACK_LAYER)) map.removeLayer(TRACK_LAYER)
      if (map.getLayer(TRACK_CASING_LAYER)) map.removeLayer(TRACK_CASING_LAYER)
      if (map.getSource(TRACK_SOURCE)) map.removeSource(TRACK_SOURCE)
      return
    }
    // Filter non-finite track points before they reach Mapbox. A single
    // `{lat: undefined}` survivor would yield `[[Infinity, Infinity],
    // [-Infinity, -Infinity]]` from `trackBounds` and crash `fitBounds`
    // downstream with the classic `reading 'lng'` throw.
    const pts: LocalTrackPoint[] = geometry.line
      .filter((p) => isFiniteLngLat(p.lon, p.lat))
      .map((p) => ({
        lat: p.lat,
        lon: p.lon,
        ele: p.ele,
      }))
    if (pts.length < 2) return
    const fc = toColouredSegments(smoothPathElevation(pts))
    if (map.getSource(TRACK_SOURCE)) {
      ;(map.getSource(TRACK_SOURCE) as mapboxgl.GeoJSONSource).setData(fc)
    } else {
      map.addSource(TRACK_SOURCE, {
        type: 'geojson',
        data: fc,
        // Each feature is one adjacent-point segment so it can carry its own
        // grade. Mapbox's default tolerance can discard an entire short
        // LineString at the route's fitted zoom instead of merely simplifying
        // it, making dense tracks disappear until the user zooms in.
        tolerance: 0,
      })
    }
    if (!map.getLayer(TRACK_CASING_LAYER)) {
      // slot:'middle' keeps the casing above road/water but under labels —
      // matches the Mapbox-recommended placement for user-data overlays.
      // line-emissive-strength tells Standard's 3D lighting engine to treat
      // the halo as a partially self-lit element so it doesn't get dimmed
      // at dusk / night light presets.
      map.addLayer({
        id: TRACK_CASING_LAYER,
        type: 'line',
        source: TRACK_SOURCE,
        slot: 'middle',
        paint: {
          'line-color': casingColorFor(effective),
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 3, 14, 7],
          'line-opacity': 1,
          'line-emissive-strength': 0.4,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      })
    } else {
      // Repaint casing in case the theme flipped since the layer was added.
      map.setPaintProperty(TRACK_CASING_LAYER, 'line-color', casingColorFor(effective))
    }
    if (!map.getLayer(TRACK_LAYER)) {
      // line-emissive-strength: 1.0 makes the grade-coloured track read as
      // self-emissive — its blue / grey / yellow / orange / red bucket
      // colours stay vivid under every Standard light preset and every
      // overlay slot's shadow casting.
      //
      // We use a Mapbox `step` paint expression keyed off the smoothed
      // per-segment grade (written by `toColouredSegments` via
      // `smoothGrades(50m)`). Buckets match `GRADE_BUCKETS` in `lib/gpx.ts`,
      // which is the single source of truth shared with `ElevationProfile`
      // so the trail section that turns red on the map turns red under the
      // elevation curve at the same X position.
      //
      // Segments without ele data (grade === null) fall back to the
      // trail-status colour from `lib/trailColor.ts` so the polyline still
      // reads as a "trail" rather than collapsing to the < −7% bucket.
      const fallbackColor = TRAIL_COLOR_PUBLISHED
      const stepArgs: (string | number)[] = [GRADE_BUCKETS[0]!.color]
      for (let i = 0; i < GRADE_BUCKET_STOPS.length; i++) {
        stepArgs.push(GRADE_BUCKET_STOPS[i]!, GRADE_BUCKETS[i + 1]!.color)
      }
      const lineColorExpr: mapboxgl.Expression = [
        'case',
        ['==', ['get', 'grade'], null],
        fallbackColor,
        ['step', ['get', 'grade'], ...stepArgs],
      ] as mapboxgl.Expression
      map.addLayer({
        id: TRACK_LAYER,
        type: 'line',
        source: TRACK_SOURCE,
        slot: 'middle',
        paint: {
          'line-color': lineColorExpr,
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 2, 14, 5],
          'line-opacity': 1,
          'line-emissive-strength': 1.0,
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
      })
    }
    // Selection overlay: idempotently add an always-present source + 2 line
    // layers + an endpoint circle layer for the brushed elevation range. The
    // actual feature data is written by the `selectedRangeKm` effect below;
    // here we just ensure the layers exist so they survive style swaps. They
    // sit above TRACK_LAYER so the brand highlight reads on top of the
    // grade-coloured base. See `wiki/entities/web-route-detail-page.md`
    // §Elevation segment selection.
    if (!map.getSource(TRACK_SELECTION_SOURCE)) {
      map.addSource(TRACK_SELECTION_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!map.getSource(TRACK_SELECTION_ENDS_SOURCE)) {
      map.addSource(TRACK_SELECTION_ENDS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })
    }
    if (!map.getLayer(TRACK_SELECTION_CASING_LAYER)) {
      map.addLayer({
        id: TRACK_SELECTION_CASING_LAYER,
        type: 'line',
        source: TRACK_SELECTION_SOURCE,
        slot: 'middle',
        paint: {
          'line-color': '#ffffff',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 7, 14, 12],
          'line-opacity': 0.95,
          'line-emissive-strength': 0.6,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      })
    }
    if (!map.getLayer(TRACK_SELECTION_LAYER)) {
      map.addLayer({
        id: TRACK_SELECTION_LAYER,
        type: 'line',
        source: TRACK_SELECTION_SOURCE,
        slot: 'middle',
        paint: {
          'line-color': '#2563eb',
          'line-width': ['interpolate', ['linear'], ['zoom'], 10, 5, 14, 9],
          'line-opacity': 0.9,
          'line-emissive-strength': 1.0,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
      })
    }
    if (!map.getLayer(TRACK_SELECTION_ENDS_LAYER)) {
      map.addLayer({
        id: TRACK_SELECTION_ENDS_LAYER,
        type: 'circle',
        source: TRACK_SELECTION_ENDS_SOURCE,
        slot: 'middle',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 14, 6],
          'circle-color': '#2563eb',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-emissive-strength': 1.0,
        },
      })
    }

    const bounds = trackBounds(pts)
    if (
      bounds &&
      isFiniteLngLat(bounds[0][0], bounds[0][1]) &&
      isFiniteLngLat(bounds[1][0], bounds[1][1]) &&
      fittedGeometryRef.current !== geometry
    ) {
      // Only auto-fit when geometry actually changed (initial load, upload,
      // commit). Style swaps keep the user's current pan / zoom.
      fittedGeometryRef.current = geometry
      try {
        map.fitBounds(bounds, { padding: 60, duration: 600 })
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RouteMap] fitBounds failed', { bounds, err })
      }
    }
  }

  // External focus request: fly to the waypoint and open its popup.
  // In read-mode pin/unpin must not move the map; edit-mode focus still
  // flies so the active draggable marker is reachable.
  useEffect(() => {
    if (!focusWaypoint) return
    if (!editing && pinnedWaypoints !== undefined) return
    const map = mapRef.current
    if (!map) return
    const wp = liveWaypoints.find((w) => w.id === focusWaypoint.id)
    if (!wp || !isFinitePoint(wp)) return
    try {
      map.flyTo({ center: [wp.lon, wp.lat], zoom: Math.max(map.getZoom(), 15), duration: 500 })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[RouteMap] flyTo focusWaypoint failed', { wp, err })
      return
    }
    requestAnimationFrame(() => {
      const m = markersRef.current.get(wp.id)
      // In edit mode the marker has no popup attached (see waypoint
      // render loop) — focusing from the right-rail list should only
      // pan/zoom the map, never open a popup that competes with the
      // already-active row editor.
      if (!m || editing) return
      if (!m.getPopup()?.isOpen()) m.togglePopup()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusWaypoint?.id, focusWaypoint?.nonce, liveWaypoints, editing, pinnedWaypoints])

  useEffect(() => {
    return () => {
      for (const m of markersRef.current.values()) m.remove()
      markersRef.current.clear()
      waypointDragHandlersRef.current.clear()
      cursorMarkerRef.current?.remove()
      cursorMarkerRef.current = null
      replayMarkerRef.current?.remove()
      replayMarkerRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [])

  return (
    <div className="xr-map-chrome xr-route-map flex h-full min-h-0 flex-col">
      <div className="relative flex min-h-[300px] flex-1 overflow-hidden bg-[var(--xr-bg-surface-elev)]">
        <div ref={containerRef} className="h-full w-full" />

        {/* Floating controls: top-left style switcher + basemap provider
         * toggle (when token available). Route editing now lives in the
         * dedicated RouteEditor surface rather than an in-map view/edit
         * toggle. */}
        <div className="pointer-events-none absolute inset-x-2 top-2 z-10 flex flex-wrap items-start justify-between gap-2">
          <div className="pointer-events-auto flex flex-col items-start gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <MapStyleSwitcher value={style} onChange={setStyle} />
            </div>
          </div>
          <div className="pointer-events-auto">
            <BasemapToggle />
          </div>
        </div>

        {/* Only surface errors as floating text. Editing instructions etc.
         * stay out of the canvas — the toggles up top are self-explanatory. */}
        {error && (
          <div className="pointer-events-none absolute inset-x-2 bottom-10 z-10 flex flex-col gap-1">
            <p className="pointer-events-auto rounded-[var(--xr-radius-sm)] bg-[var(--xr-danger-soft)] px-2 py-1 text-xs text-[var(--xr-danger)]">
              {error}
            </p>
          </div>
        )}

        {/* Vertical grade gradient legend along the left edge of the map.
         * Bottom-aligned with the right-side NavigationControl (bottom: 10px).
         * Fixed height so labels stay legible. */}
        {
          <div
            className="pointer-events-none absolute bottom-[44px] left-[10px] z-10 flex w-6 items-center"
            style={{ height: '220px' }}
            aria-hidden
          >
            <GradeGradientBar />
          </div>
        }
      </div>
    </div>
  )

  function makeWaypointPopup(wp: Waypoint): mapboxgl.Popup {
    const div = document.createElement('div')
    div.className = 'xr-wpt-popup'
    const title = document.createElement('div')
    title.className = 'text-sm font-medium'
    title.textContent = wp.name ?? '(unnamed)'
    div.appendChild(title)
    if (wp.desc) {
      const desc = document.createElement('div')
      desc.className = 'text-xs mt-1'
      desc.textContent = wp.desc
      div.appendChild(desc)
    }
    return new mapboxgl.Popup({ offset: 24, closeButton: false }).setDOMContent(div)
  }
}

// Left-edge grade legend rendered as 6 discrete swatches inside the map
// container, matching the polyline's bucket palette and the elevation
// profile's per-segment fills. Vertical layout, text rotated -90° so labels
// read bottom-to-top (flat → steep).
function GradeGradientBar() {
  const { t } = useLocale()
  const { effective } = useTheme()
  const labelCls =
    effective === 'dark'
      ? 'text-[8px] font-medium leading-none text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.65)] tabular-nums'
      : 'text-[8px] font-medium leading-none text-[var(--xr-text-primary)] [text-shadow:0_1px_2px_rgba(255,255,255,0.85)] tabular-nums'
  return (
    <div
      className="flex h-full w-full flex-col-reverse items-center gap-1"
      data-testid="route-map-grade-legend"
      data-orientation="vertical"
      aria-label={t('aria.gradeLegend')}
    >
      {GRADE_BUCKETS.map((bucket) => (
        <div
          key={bucket.label}
          className="flex flex-1 flex-row items-center gap-0.5 min-h-0 w-full"
        >
          <span
            className="w-1.5 h-full rounded-[var(--xr-radius-full)]"
            style={{ background: bucket.color }}
            aria-hidden
          />
          <span className={`${labelCls} [writing-mode:vertical-rl] rotate-180 whitespace-nowrap`}>
            {bucket.label}
          </span>
        </div>
      ))}
    </div>
  )
}
